import { useState } from "react";
import { useNavigate } from "react-router-dom";
import amanImg from "../../../assets/Aman1.png";
import familyImg from "../../../assets/img84.webp";
import { CheckCircle } from "lucide-react";
import Testimonials from "../../../components/Testimonials";
import SEO from "../../../components/SEO";

const LifeInsuranceEebook = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  // null | "loading" | "success" | "error"
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const MAX_NAME_LEN = 32;

  const isValidEmail = (email) => {
    const v = String(email || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v);
  };

  const normalizeUniversalPhone = (phone) => {
    let digits = String(phone || "").replace(/\D/g, "");

    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);

    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);

    if (digits.length === 10 && /^[2-9]/.test(digits)) {
      return digits;
    }
    return "";
  };

  const formatPhoneForDisplay = (digits10) => {
    if (!digits10 || digits10.length !== 10) return digits10;
    return `(${digits10.slice(0, 3)}) ${digits10.slice(3, 6)}-${digits10.slice(6)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let next = value;

    if (name === "name") {
      next = String(value || "").slice(0, MAX_NAME_LEN);
    }

    if (name === "phone") {
      next = String(value || "").replace(/[^0-9+\-\s().]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: next }));

    if (status) setStatus(null);
    if (errorMsg) setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();

    if (!name || !email || !phone) {
      setStatus("error");
      setErrorMsg("Veuillez remplir tous les champs.");
      return;
    }

    if (name.length > MAX_NAME_LEN) {
      setStatus("error");
      setErrorMsg("Le nom doit comporter 32 caractères ou moins.");
      return;
    }

    if (!isValidEmail(email)) {
      setStatus("error");
      setErrorMsg("Veuillez entrer une adresse courriel valide.");
      return;
    }

    const phoneDigits = normalizeUniversalPhone(phone);
    if (!phoneDigits) {
      setStatus("error");
      setErrorMsg("Veuillez entrer un numéro de téléphone valide à 10 chiffres.");
      return;
    }

    try {
      setStatus("loading");
      setErrorMsg("");

      const fd = new FormData();
      fd.append("leadEmail", email);
      fd.append("fullName", name);
      fd.append("telPhone", phoneDigits);

      const BACKEND_BASE =
        import.meta.env.VITE_BACKEND_URL || "https://backend.einsured.ca";

      const res = await fetch(`${BACKEND_BASE}/api/addEbookLead`, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: fd,
      });

      const text = await res.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch {}

      if (!res.ok) {
        throw new Error(data?.message || text || "Request failed");
      }

      setStatus("success");

      setFormData({ name: "", email: "", phone: "" });

      setTimeout(() => {
        navigate("/fr/guide-assurance-vie/merci");
      }, 800);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.message || "Échec de la soumission. Veuillez réessayer.");
    }
  };

  return (
    <>
    <SEO
        title="Livre numérique gratuit sur l'assurance vie | einsured.ca Canada"
      description="Découvrez le livre numérique gratuit d’einsured.ca sur l’assurance-vie et obtenez des conseils pour choisir une couverture adaptée à vos besoins.  "
    />
      {/* HERO + AMAN + FORM */}
      <section className="relative bg-gradient-to-r from-[#3F5B83] via-[#4E6787] to-[#6E87A5]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-24">
          <div className="flex flex-col lg:flex-row min-h-[30vh] pt-10 pb-24 lg:pb-32">
            {/* Left: Heading */}
            <div className="w-full lg:w-1/2 flex items-left mt-10 ml-(-20)">
              <h1 className="text-white font-bold leading-tight text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem]">
                Vous en avez assez
                <br />
                de chercher sur
                <br />
                Internet votre
                <br />
                assurance personnelle ?
              </h1>
            </div>

            {/* Right: Aman image */}
            <div className="w-full lg:w-1/2 flex justify-center lg:justify-end items-end mt-8 lg:mt-0">
              <div className="relative z-10">
                <img
                  src={amanImg}
                  alt="Aman - Conseiller en assurance"
                  className="w-[260px] sm:w-[320px] lg:w-[360px] xl:w-[390px] h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* FORM CARD */}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-24">
          <form
            id="ebook-form"
            onSubmit={handleSubmit}
            className="
              relative z-20 w-full max-w-[500px]
              mx-auto mt-6
              bg-[#35537C] text-white
              rounded-[32px]
              shadow-[0_22px_45px_rgba(0,0,0,0.35)]
              px-8 py-10 sm:px-15 sm:py-15
              space-y-5
              lg:mx-0
              lg:absolute lg:right-[11%] lg:bottom-0 lg:translate-y-[30%]
            "
          >
            <div className="space-y-1">
              <p className="text-lg sm:text-xl font-semibold leading-snug">
                Pour accéder instantanément au
                <br />
                livre, remplissez le formulaire
              </p>
            </div>

            {/* Name */}
            <div>
              <input
                type="text"
                name="name"
                placeholder="Nom"
                maxLength={32}
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full rounded-md bg-white text-[#333] text-base sm:text-lg px-4 py-3 border border-transparent focus:outline-none focus:ring-2 focus:ring-[#00A8E8]"
              />
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                name="email"
                placeholder="Courriel"
                value={formData.email}
                onChange={handleChange}
                onBlur={() =>
                  setFormData((p) => ({
                    ...p,
                    email: String(p.email || "").trim(),
                  }))
                }
                required
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]{2,}$"
                className="w-full rounded-md bg-white text-[#333] text-base sm:text-lg px-4 py-3 border border-transparent focus:outline-none focus:ring-2 focus:ring-[#00A8E8]"
              />
            </div>

            {/* Phone */}
            <div>
              <input
                type="tel"
                name="phone"
                placeholder="Numéro de téléphone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={() => {
                  const digits = normalizeUniversalPhone(formData.phone);
                  if (digits) {
                    setFormData((p) => ({
                      ...p,
                      phone: formatPhoneForDisplay(digits),
                    }));
                  }
                }}
                required
                pattern="^[0-9+\-\s()]{10,20}$"
                inputMode="tel"
                className="w-full rounded-md bg-white text-[#333] text-base sm:text-lg px-4 py-3 border border-transparent focus:outline-none focus:ring-2 focus:ring-[#00A8E8]"
              />
            </div>

            {/* Button */}
            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-3 w-full rounded-md bg-[#00A8E8] py-3.5 text-base sm:text-lg font-semibold shadow-md hover:bg-[#0093cf] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {status === "loading" ? "Envoi en cours..." : "Accès instantané"}
            </button>

            {status === "error" && (
              <p className="pt-2 text-sm text-red-200 text-center">
                {errorMsg || "Une erreur est survenue."}
              </p>
            )}
            {status === "success" && (
              <p className="pt-2 text-sm text-emerald-200 text-center">
                Merci ! Votre demande a bien été reçue.
              </p>
            )}
          </form>
        </div>
      </section>

      {/* WHY YOU CAN'T MISS THIS OPPORTUNITY */}
      <section className="bg-white pt-40 pb-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-16 lg:px-4">
          <div className="text-center mb-14">
            <h2 className="text-[2.4rem] sm:text-[2.8rem] md:text-[3rem] font-semibold leading-tight text-[#49628b]">
              Pourquoi vous ne
              <br />
              pouvez pas manquer
              <br />
              cette opportunité
            </h2>
          </div>

          {/* Cards */}
          <div className="space-y-8">
            {/* Row 1 */}
            <div className="grid gap-7 md:grid-cols-3">
              {/* Empower Yourself */}
              <div className="rounded-[26px] bg-[#F7F8FA] px-9 py-9 shadow-[0_18px_40px_rgba(0,0,0,0.05)]">
                <div className="mb-4">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#0BAEEF] text-white text-2xl">
                    ⚡
                  </div>
                </div>
                <h3 className="mb-3 text-[1.45rem] font-semibold text-[#244065]">
                  Prenez le contrôle
                </h3>
                <p className="text-[0.98rem] leading-relaxed text-[#4E5B78]">
                  L'assurance vie n'a pas à être compliquée. Plongez dans notre livre numérique pour démêler les complexités, comprendre les nuances et vous armer de connaissances.
                </p>
              </div>

              {/* Comprehensive Coverage */}
              <div className="rounded-[26px] bg-[#F7F8FA] px-9 py-9 shadow-[0_18px_40px_rgba(0,0,0,0.05)]">
                <div className="mb-4">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#0BAEEF] text-white text-2xl">
                    ðŸ“‹
                  </div>
                </div>
                <h3 className="mb-3 text-[1.45rem] font-semibold text-[#244065]">
                  Couverture complète
                </h3>
                <p className="text-[0.98rem] leading-relaxed text-[#4E5B78]">
                  Explorez les différents types d'assurance vie et comprenez en profondeur quelle police correspond à vos besoins et circonstances uniques. Il ne s'agit pas seulement de couverture ; il s'agit de solutions sur mesure.
                </p>
              </div>

              {/* Strategic Financial Planning */}
              <div className="rounded-[26px] bg-[#F7F8FA] px-9 py-9 shadow-[0_18px_40px_rgba(0,0,0,0.05)]">
                <div className="mb-4">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#0BAEEF] text-white text-2xl">
                    ðŸ“Š
                  </div>
                </div>
                <h3 className="mb-3 text-[1.45rem] font-semibold text-[#244065]">
                  Planification financière stratégique
                </h3>
                <p className="text-[0.98rem] leading-relaxed text-[#4E5B78]">
                  L'assurance vie est bien plus qu'un filet de sécurité — c'est un outil stratégique de planification financière. Apprenez à intégrer l'assurance vie de manière transparente dans votre stratégie financière pour construire des bases solides pour l'avenir.
                </p>
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid gap-7 md:grid-cols-2 md:max-w-4xl md:mx-auto">
              {/* FREE Access to Wisdom */}
              <div className="rounded-[26px] bg-[#F7F8FA] px-9 py-9 shadow-[0_18px_40px_rgba(0,0,0,0.05)]">
                <div className="mb-4">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#0BAEEF] text-white text-2xl">
                    ðŸŽ“
                  </div>
                </div>
                <h3 className="mb-3 text-[1.45rem] font-semibold text-[#244065]">
                  Accès GRATUIT à la sagesse
                </h3>
                <p className="text-[0.98rem] leading-relaxed text-[#4E5B78]">
                  Ce livre numérique ne se contente pas de fournir des informations ; il transmet une sagesse qui peut transformer votre façon de penser et d'aborder l'assurance vie. Et le meilleur ? C'est absolument GRATUIT !
                </p>
              </div>

              {/* Expert Guidance */}
              <div className="rounded-[26px] bg-[#F7F8FA] px-9 py-9 shadow-[0_18px_40px_rgba(0,0,0,0.05)]">
                <div className="mb-4">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#0BAEEF] text-white text-2xl">
                    ðŸ‘¤
                  </div>
                </div>
                <h3 className="mb-3 text-[1.45rem] font-semibold text-[#244065]">
                  Conseils d'experts
                </h3>
                <p className="text-[0.98rem] leading-relaxed text-[#4E5B78]">
                  Bénéficiez de l'expertise de professionnels chevronnés qui ont distillé leurs connaissances dans ce guide complet. Notre livre numérique est votre ressource incontournable pour prendre des décisions éclairées concernant votre avenir financier.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WE HAVE SOMETHING FOR YOU */}
      <section className="bg-white pb-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          {/* Top heading */}
          <div className="text-center mb-10">
            <h3 className="text-[2rem] sm:text-[2.3rem] md:text-[2rem] font-semibold leading-snug text-[#49628b]">
              Nous avons quelque chose pour vous. Découvrez les secrets d'une découverte d'assurance sans effort ! Plongez dans notre livre numérique GRATUIT et explorez le monde de la sécurité financière.
            </h3>
          </div>

          <div className="rounded-[40px] bg-[#395680] text-white overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.25)]">
            <div className="relative flex flex-col md:flex-row">
              <div className="pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-[#395680]/45 via-[#395680]/25 to-transparent" />

              {/* Left: image */}
              <div className="md:w-1/2">
                <img
                  loading="lazy"
                  src={familyImg}
                  alt="Famille heureuse avec une maison"
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Right: text */}
              <div className="md:w-1/2 flex flex-col justify-center px-8 py-10 sm:px-10 sm:py-12 lg:px-14 lg:py-14">
                <ul className="space-y-4 text-[1.05rem] sm:text-[1.1rem] leading-relaxed">
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>Accédez gratuitement au livre numérique sur l'assurance vie.</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Dites adieu à la longue quête sur Internet pour trouver la couverture parfaite.
                    </p>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle
                      aria-hidden="true"
                      className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                      strokeWidth={2.2}
                    />
                    <p>
                      Votre parcours d'assurance sans stress commence{" "}
                      <span className="font-semibold">ici</span>.
                    </p>
                  </li>
                </ul>

                {/* Button */}
                <div className="mt-8">
                  <a
                    href="#ebook-form"
                    className="inline-flex w-full md:w-auto items-center justify-center rounded-md bg-[#00A8E8] px-8 py-3.5 text-base sm:text-lg font-semibold shadow-md hover:bg-[#0093cf] transition-colors"
                  >
                    Téléchargez maintenant pour un accès instantané !
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials limit={9} autoPlay={true} intervalMs={4500} />
    </>
  );
};

export default LifeInsuranceEebook;
